import { Link } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import StageTracker from "@/components/base/StageTracker";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/hooks/useApi";

interface ApiProject {
  id: string;
  title: string;
  stage: string;
  status: string;
  progress: number;
  due_date: string | null;
  client_id: string;
}

interface ApiTicket {
  id: string;
  subject: string;
  priority: string;
  status: string;
}

interface ApiInvoice {
  id: string;
  number: string;
  description: string | null;
  amount: number;
  status: string;
}

interface ApiSubscription {
  id: string;
  plan: string;
  status: string;
  amount: number;
  billing_cycle: string;
}

interface ClientDashboardData {
  projects: ApiProject[];
  tickets: ApiTicket[];
  invoices: ApiInvoice[];
  subscription: ApiSubscription | null;
}

function StatCard({ icon, label, value, sub, color }: { icon: string; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl p-5 hover:border-electric-blue/20 transition-all">
      <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-dark-panel border border-dark-border mb-4 ${color}`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div className="text-2xl font-black text-white mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</div>
      <div className="text-silver text-sm">{label}</div>
      {sub && <div className="text-silver-dim text-xs mt-1">{sub}</div>}
    </div>
  );
}

export default function DashboardOverview() {
  const { user } = useAuth();
  const { data, loading } = useApi<ClientDashboardData>("/dashboard/client");

  const activeProjects = data?.projects?.filter((p) => p.status === "active") || [];
  const openTickets = data?.tickets?.filter((t) => t.status === "open" || t.status === "in_progress") || [];
  const unpaidInvoices = data?.invoices?.filter((i) => i.status === "sent" || i.status === "overdue") || [];
  const subscription = data?.subscription;

  const stageIndex = (stage: string) => {
    const stages = ["kickoff", "design", "development", "testing", "launch"];
    return stages.indexOf(stage);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-silver text-sm mt-1">Here's what's happening with your projects.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon="ri-folder-line" label="Active Projects" value={String(activeProjects.length)} color="text-electric-blue" />
          <StatCard icon="ri-customer-service-line" label="Open Tickets" value={String(openTickets.length)} color="text-cyber-orange" />
          <StatCard icon="ri-file-list-3-line" label="Pending Invoices" value={String(unpaidInvoices.length)} sub={`$${unpaidInvoices.reduce((s, i) => s + i.amount, 0).toLocaleString()} due`} color="text-cyber-red" />
          <StatCard icon="ri-vip-crown-line" label="Current Plan" value={subscription ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1) : "Free"} sub={subscription ? `$${subscription.amount}/mo` : ""} color="text-cyber-green" />
        </div>

        <div className="bg-dark-card border border-dark-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Active Projects</h2>
            <Link to="/dashboard/projects" className="text-electric-blue text-xs hover:underline cursor-pointer whitespace-nowrap">View all</Link>
          </div>
          <div className="space-y-5">
            {activeProjects.length === 0 && (
              <p className="text-silver-dim text-sm">No active projects.</p>
            )}
            {activeProjects.map((project) => (
              <Link key={project.id} to={`/dashboard/projects/${project.id}`} className="block group">
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div>
                    <h3 className="text-white text-sm font-semibold group-hover:text-electric-blue transition-colors">{project.title}</h3>
                    <p className="text-silver-dim text-xs mt-0.5">{project.due_date ? `Due ${new Date(project.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-cyber-green/10 border border-cyber-green/20 text-cyber-green font-medium whitespace-nowrap">{project.progress}%</span>
                  </div>
                </div>
                <StageTracker currentStage={project.stage} currentIndex={stageIndex(project.stage)} />
                <div className="mt-3 w-full bg-dark-border rounded-full h-1.5">
                  <div className="bg-electric-blue h-1.5 rounded-full transition-all duration-700" style={{ width: `${project.progress}%` }} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-dark-card border border-dark-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Recent Tickets</h2>
              <Link to="/dashboard/tickets" className="text-electric-blue text-xs hover:underline cursor-pointer whitespace-nowrap">View all</Link>
            </div>
            <div className="space-y-3">
              {data?.tickets?.slice(0, 3).map((ticket) => {
                const statusColors: Record<string, string> = { open: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10", in_progress: "text-electric-blue border-electric-blue/30 bg-electric-blue/10", resolved: "text-cyber-green border-cyber-green/30 bg-cyber-green/10", closed: "text-silver-dim border-dark-border bg-dark-border/30" };
                return (
                  <Link key={ticket.id} to="/dashboard/tickets" className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-dark-panel transition-colors cursor-pointer group">
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate group-hover:text-electric-blue transition-colors">{ticket.subject}</p>
                      <p className="text-silver-dim text-xs mt-0.5">{ticket.priority} priority</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${statusColors[ticket.status]}`}>
                      {ticket.status.replace("_", " ")}
                    </span>
                  </Link>
                );
              }) || <p className="text-silver-dim text-sm">No tickets yet.</p>}
            </div>
          </div>

          <div className="bg-dark-card border border-dark-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Recent Invoices</h2>
              <Link to="/dashboard/billing" className="text-electric-blue text-xs hover:underline cursor-pointer whitespace-nowrap">View all</Link>
            </div>
            <div className="space-y-3">
              {data?.invoices?.slice(0, 3).map((inv) => {
                const statusColors: Record<string, string> = { paid: "text-cyber-green", sent: "text-cyber-orange", overdue: "text-cyber-red", draft: "text-silver-dim" };
                return (
                  <div key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-dark-panel transition-colors">
                    <div>
                      <p className="text-white text-xs font-medium">{inv.number}</p>
                      <p className="text-silver-dim text-xs mt-0.5">{inv.description || "Invoice"}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-white text-xs font-bold">${inv.amount.toLocaleString()}</p>
                      <p className={`text-xs capitalize ${statusColors[inv.status]}`}>{inv.status}</p>
                    </div>
                  </div>
                );
              }) || <p className="text-silver-dim text-sm">No invoices yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

